import { Chip } from "@mui/material";
import { ObjectValues } from "../login";

import {
  School as SchoolIcon,
  Flag as FlagIcon,
  Category as CategoryIcon,
  MenuBook as MenuBookIcon,
  LocalLibrary as LocalLibraryIcon,
  LabelImportant as LabelImportantIcon,
  Layers as LayersIcon,
  Quiz as QuizIcon,
  Delete,
} from "@mui/icons-material";
import { fuzzySearch } from "../../Components/CustomSearch";
// ---------- Types ----------
export type NodeType =
  | "EXAM"
  | "GOAL"
  | "SECTION"
  | "UNIT"
  | "TOPIC"
  | "SUBTOPIC"
  | "CLUSTER"
  | "QUESTION";
export const chipConfigs = (data: any) =>
  [
    (data.total_goals || data.total_goals === 0) && {
      key: "goal",
      label: "Goals",
      value: data.total_goals,
      color: "#eff6ff",
      text: "#2563eb",
    },
    (data.total_sections || data.total_sections === 0) && {
      key: "section",
      label: "Sections",
      value: data.total_sections,
      color: "#f0fdf4",
      text: "#16a34a",
    },
    (data.total_units || data.total_units === 0) && {
      key: "unit",
      label: "Units",
      value: data.total_units,
      color: "#faf5ff",
      text: "#9333ea",
    },
    (data.total_topics || data.total_topics === 0) && {
      key: "topic",
      label: "Topics",
      value: data.total_topics,
      color: "#fff7ed",
      text: "#ea580c",
    },
    (data.total_subtopics || data.total_subtopics === 0) && {
      key: "subtopic",
      label: "Subtopics",
      value: data.total_subtopics,
      color: "#fef2f2",
      text: "#dc2626",
    },
    (data.total_clusters || data.total_clusters === 0) && {
      key: "cluster",
      label: "Clusters",
      value: data.total_clusters,
      color: "#fef3c7",
      text: "#d97706",
    },
    data.total_questions && {
      key: "question",
      label: "Questions",
      value: data.total_questions,
      color: "#eef2ff",
      text: "#4f46e5",
    },
  ].filter((s) => s);
export const getNextPath = (type: NodeType) => {
  switch (type) {
    case "EXAM":
      return "GOAL";
    case "GOAL":
      return "SECTION";
    case "SECTION":
      return "UNIT";
    case "UNIT":
      return "TOPIC";
    case "TOPIC":
      return "SUBTOPIC";
    case "SUBTOPIC":
      return "CLUSTER";
    case "CLUSTER":
      return "QUESTION";
    default:
      return "EXAM";
  }
};

export const getParentPath = (type: NodeType) => {
  switch (type) {
    case "GOAL":
      return "EXAM";
    case "SECTION":
      return "GOAL";
    case "UNIT":
      return "SECTION";
    case "TOPIC":
      return "UNIT";
    case "SUBTOPIC":
      return "TOPIC";
    case "CLUSTER":
      return "SUBTOPIC";
    case "QUESTION":
      return "CLUSTER";
    default:
      return "EXAM";
  }
};
export const getParentSmallPath = (type: string) => {
  switch (type) {
    case "goal":
      return "exam";
    case "section":
      return "goal";
    case "unit":
      return "section";
    case "topic":
      return "unit";
    case "subtopic":
      return "topic";
    case "cluster":
      return "subtopic";
    case "question":
      return "cluster";
    default:
      return "exam";
  }
};
export type Node = {
  id: string;
  label: string;
  type: NodeType;
  status?: "Draft" | "Published";
  children?: Node[];
  details: ObjectValues;
};

// ---------- Helpers / sample data ----------
export const ICON_BY_TYPE: any = {
  EXAM: <SchoolIcon fontSize="small" />,
  GOAL: <FlagIcon fontSize="small" />,
  SECTION: <CategoryIcon fontSize="small" />,
  UNIT: <MenuBookIcon fontSize="small" />,
  TOPIC: <LocalLibraryIcon fontSize="small" />,
  SUBTOPIC: <LabelImportantIcon fontSize="small" />,
  CLUSTER: <LayersIcon fontSize="small" />,
  QUESTION: <QuizIcon fontSize="small" />,
};
export const colors: any = {
  EXAM: "#e5e7eb",
  GOAL: "#2563eb",
  SECTION: "#16a34a",
  UNIT: "#9333ea",
  TOPIC: "#ea580c",
  SUBTOPIC: "#dc2626",
  CLUSTER: "#d97706",
  QUESTION: "#4f46e5",
};
export const bgcolors: any = {
  EXAM: "white",
  GOAL: "#eff6ff",
  SECTION: "#f0fdf4",
  UNIT: "#faf5ff",
  TOPIC: "#fff7ed",
  SUBTOPIC: "#fef2f2",
  CLUSTER: "#fef3c7",
  QUESTION: "#4f46e5",
};

// ---------- Small UI pieces ----------
export function TypeChip({ type }: { type: NodeType }) {
  return (
    <Chip
      size="small"
      label={type === "EXAM" ? "Exams" : `${type}s`}
      icon={ICON_BY_TYPE[type]}
      sx={{
        textTransform: "uppercase",
        fontWeight: 700,
        height: 24,
        padding: "0 7px",
        "& .MuiChip-label": { px: 0.5 },
        "& .MuiSvgIcon-root": { margin: "0 !important" },
      }}
    />
  );
}
export const serachFilterFunction = (list: ObjectValues[], search: any) => {
  const fieldName = ["label"];
  const filterData: ObjectValues[] = [];
  if (list && fieldName && search) {
    list.map((data: ObjectValues) => {
      return fieldName.map((singleField) => {
        if (
          (data[singleField] &&
            data[singleField]
              .toString()
              .toLowerCase()
              .includes(search.toString().toLowerCase())) ||
          fuzzySearch(search.toString().toLowerCase(), data[singleField])
        ) {
          if (!filterData.includes(data)) {
            return filterData.push(data);
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
    });
    return filterData;
  } else {
    return list;
  }
};
export const formatNestedList = (lists: any[], level: any) => {
  return lists.map((s) => {
    return {
      id: s.id,
      label: s.name ?? `#${s.id}`,
      type: level,
      status: s.status,
      details: s,
      children:
        level === "EXAM"
          ? s.total_goals > 0
            ? []
            : undefined
          : level === "GOAL"
          ? s.total_sections > 0
            ? []
            : undefined
          : level === "SECTION"
          ? s.total_units > 0
            ? []
            : undefined
          : level === "UNIT"
          ? s.total_topics > 0
            ? []
            : undefined
          : level === "TOPIC"
          ? s.total_subtopics > 0
            ? []
            : undefined
          : level === "SUBTOPIC"
          ? s.total_clusters > 0
            ? []
            : undefined
          : level === "CLUSTER"
          ? s.total_questions > 0
            ? []
            : undefined
          : [],
    };
  });
};
export interface Translation {
  id: number;
  status: string;
  parent_id: number;
  text: string;
  language: string;
  media_urls: string[] | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
  modified_at: string;
  parent_model: number;
  created_by: number;
  modified_by: number;
}

export interface Explanation {
  id: number;
  translation: Translation[];
  status: string;
  parent_id: number;
  default_language: string;
  source: string;
  quality_score: number | null;
  prompt_used: string | null;
  prompt_count: number | null;
  is_active: boolean;
  created_at: string;
  modified_at: string;
  parent_model: number;
  created_by: number;
  modified_by: number;
}

export interface Option {
  id: number;
  translation: Translation[];
  status: string;
  order: number;
  is_correct: boolean;
  is_active: boolean;
  default_language: string;
  created_at: string;
  modified_at: string;
  old_id: number | null;
  question: number;
  created_by: number;
  modified_by: number;
}

export interface Question {
  id: number;
  translation: Translation[];
  explanation: Explanation;
  options: Option[];
  tag: any[]; // replace `any[]` with a proper Tag interface if you have it
  status: string;
  default_language: string;
  can_be_ready: boolean;
  can_be_approved: boolean;
  v2_to_v3: any | null;
  mockqc_status: any | null;
  is_active: boolean;
  created_at: string;
  modified_at: string;
  old_id: number | null;
  is_proofread_hi: boolean;
  question_type: string | null;
  cluster: number;
  created_by: number;
  modified_by: number;
}
